import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import {
  OP_WORDS,
  OPS_BY_TYPE,
  PREDICATE_MEMBERS,
  emptyValue,
  isListOp,
  predicateMemberType,
  type IPredicateLeaf,
  type PredicateMemberType,
  type PredicateOp
} from '../../concern-predicate';
import { disclosureField, disclosureFieldSection, sectionTitle } from '../../disclosure-fields';
import { SELECT_NONE } from '../staff-fields/choices';
import { TagInput } from './tag-input';

/**
 * One condition of a rule — Field, Operator, Value and a remove (SR-227, R7, design
 * `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md` §3.3).
 *
 * **The controls are shaped by the mirror and judge nothing**, `field-constraints.ts`' kind of
 * work: the Field select offers the nine members survey-api's parser reads, the Operator select
 * the operators that member's type takes, and the Value control is the one the operator wants —
 * a date input, a dollar box, a text box, a list, or nothing for "is blank". What is typed is
 * held as typed; `writePredicate` composes it and survey-api's answer lands here, at the row's
 * path (`[data-condition]`), the text kept. The rejected alternative — refusing `abc` in the
 * dollar box before Save — would be the second parser the mirror exists not to be.
 *
 * **A field of another type starts the operator and the value again**; within a type both stay,
 * so swapping Settlement Amount for Damages Granted keeps "is at least $100,000".
 *
 * A Text value is offered the values other criteria compare that member to ("Used by other
 * criteria"), read off the page's own list — free text all the same.
 */

/** The nine members grouped by the disclosure section they sit in, sections in the record
 *  page's order and members in the parser's — what the Field select offers. */
const FIELD_GROUPS: readonly { title: string; members: string[] }[] = (() => {
  const groups = new Map<string, string[]>();
  for (const member of PREDICATE_MEMBERS.keys()) {
    const title = sectionTitle(disclosureFieldSection(member) ?? 'Other');
    groups.set(title, [...(groups.get(title) ?? []), member]);
  }
  return [...groups].map(([title, members]) => ({ title, members }));
})();

/** What survey-api's `Test` does with a value, said once under it in a reader's terms. */
const VALUE_HINT: Partial<Record<PredicateMemberType, string>> = {
  text: 'Compared ignoring case and extra spaces.',
  words: 'Whole words, ignoring case.'
};

export function ConditionRow({
  leaf,
  number,
  path,
  problems,
  suggestions,
  onChange,
  onRemove
}: {
  leaf: IPredicateLeaf;
  /** The row's ordinal across the whole rule, in reading order — its controls' names. */
  number: number;
  /** The JSON path survey-api answers this condition at (`predicate.all[0]`). */
  path: string;
  problems: readonly string[];
  /** Values other criteria compare a member to, by member. */
  suggestions: ReadonlyMap<string, readonly string[]>;
  onChange: (leaf: IPredicateLeaf) => void;
  onRemove: () => void;
}) {
  const listId = React.useId();
  const type = predicateMemberType(leaf.member);
  const ops = type ? OPS_BY_TYPE[type] : [];
  const label = `Condition ${number}`;
  const invalid = problems.length > 0;
  const offered = type === 'text' ? (suggestions.get(leaf.member) ?? []) : [];

  const chooseMember = (member: string) => {
    const next = predicateMemberType(member);
    if (next === type) {
      onChange({ ...leaf, member });
      return;
    }
    const op = next ? OPS_BY_TYPE[next][0] : '';
    onChange({ ...leaf, member, op, value: emptyValue(op) });
  };

  const chooseOp = (op: string) => {
    // A value carries over where its shape does — one text to another, one list to another.
    const keeps =
      op !== 'blank' && leaf.op !== 'blank' && leaf.op !== '' && isListOp(op) === isListOp(leaf.op);
    onChange({ ...leaf, op, value: keeps ? leaf.value : emptyValue(op) });
  };

  const text = Array.isArray(leaf.value) || leaf.value === true ? '' : String(leaf.value);
  const setText = (value: string) => onChange({ ...leaf, value });

  let value: React.ReactNode = null;
  if (leaf.op === '' || !type) {
    value = (
      <Input aria-label={`${label} — Value`} disabled value="" className="w-56" readOnly />
    );
  } else if (isListOp(leaf.op)) {
    value = (
      <TagInput
        label={`${label} — Value`}
        values={Array.isArray(leaf.value) ? leaf.value : []}
        onChange={(values) => onChange({ ...leaf, value: values })}
        listId={offered.length > 0 ? listId : undefined}
        invalid={invalid}
      />
    );
  } else if (leaf.op !== 'blank') {
    const money = disclosureField(leaf.member)?.kind === 'money';
    const box = (
      <Input
        aria-label={`${label} — Value`}
        aria-invalid={invalid || undefined}
        value={text}
        onChange={(event) => setText(event.target.value)}
        type={type === 'date' ? 'date' : 'text'}
        inputMode={type === 'number' ? 'decimal' : undefined}
        list={offered.length > 0 ? listId : undefined}
        autoComplete="off"
        className={money ? 'w-56 pl-5' : 'w-56'}
      />
    );
    value = money ? (
      <span className="relative inline-flex items-center">
        <span aria-hidden className="pointer-events-none absolute left-2 text-muted-foreground">
          $
        </span>
        {box}
      </span>
    ) : (
      box
    );
  }

  const hint = type && leaf.op !== 'blank' && leaf.op !== '' ? VALUE_HINT[type] : undefined;

  return (
    <div data-condition={path} className="grid gap-1">
      <div className="flex flex-wrap items-start gap-2">
        <Select
          value={leaf.member === '' ? SELECT_NONE : leaf.member}
          onValueChange={(member) => chooseMember(member === SELECT_NONE ? '' : member)}
        >
          <SelectTrigger aria-label={`${label} — Field`} className="w-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_NONE}>Choose a field</SelectItem>
            {/* A member the mirror lacks is never offered — a stored one draws no row (R11). */}
            {FIELD_GROUPS.map((group) => (
              <SelectGroup key={group.title}>
                <SelectLabel>{group.title}</SelectLabel>
                {group.members.map((member) => (
                  <SelectItem key={member} value={member}>
                    {disclosureField(member)?.label ?? member}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={leaf.op === '' ? SELECT_NONE : leaf.op}
          disabled={!type}
          onValueChange={(op) => chooseOp(op === SELECT_NONE ? '' : op)}
        >
          <SelectTrigger aria-label={`${label} — Operator`} className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_NONE}>Choose</SelectItem>
            {ops.map((op) => (
              <SelectItem key={op} value={op}>
                {type ? (OP_WORDS[type][op as PredicateOp] ?? op) : op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {value}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={`Remove condition ${number}`}
          onClick={onRemove}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      {offered.length > 0 && (
        <datalist id={listId} aria-label="Used by other criteria">
          {offered.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
      {(hint || offered.length > 0) && (
        <span className="text-xs text-muted-foreground">
          {hint}
          {offered.length > 0 && `${hint ? ' ' : ''}Offers the values used by other criteria.`}
        </span>
      )}
      {problems.map((reason, index) => (
        <p key={index} role="alert" className="text-destructive">
          {reason}
        </p>
      ))}
    </div>
  );
}
